import { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";

/**
 * Searchable + creatable combobox.
 *
 * Copy props (placeholder / searchPlaceholder / emptyText / createLabel /
 * existingHeading / creatingLabel) are REQUIRED to be translated by the
 * caller — per the i18n invariant we never ship hardcoded English here.
 * Fallbacks are provided only so the component fails safe at runtime.
 */
export function CreatableCombobox({
  value,
  options = [],
  onChange,
  onCreate,
  placeholder = "Select…",
  searchPlaceholder = "Search or create…",
  emptyText = "No results.",
  createLabel = "Create",
  existingHeading = "Existing",
  creatingLabel = "Creating",
  disabled = false,
  testIdPrefix = "combobox",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);

  const trimmedQuery = query.trim();
  const lowered = trimmedQuery.toLowerCase();

  const existingMatch = options.find((o) => o.toLowerCase() === lowered);
  const filtered = trimmedQuery
    ? options.filter((o) => o.toLowerCase().includes(lowered))
    : options;

  const showCreateRow = Boolean(trimmedQuery) && !existingMatch;

  const handleSelect = (picked) => {
    onChange?.(picked);
    setOpen(false);
    setQuery("");
  };

  const handleCreate = async () => {
    if (!trimmedQuery || creating) return;
    try {
      setCreating(true);
      await onCreate?.(trimmedQuery);
      onChange?.(trimmedQuery);
      setOpen(false);
      setQuery("");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={`${testIdPrefix}-trigger`}
          className="w-full justify-between bg-slate-800 border-slate-700 text-white hover:bg-slate-800/80 hover:text-white"
        >
          <span className={cn("truncate", !value && "text-slate-400")}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-slate-900 border-slate-700 w-[var(--radix-popover-trigger-width)] min-w-[240px]"
        align="start"
      >
        <Command
          className="bg-slate-900 text-white"
          filter={(item, search) => {
            if (!search) return 1;
            return item.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            data-testid={`${testIdPrefix}-search`}
            className="h-10 text-white"
          />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-sm text-slate-400">
              {emptyText}
            </CommandEmpty>
            {filtered.length > 0 && (
              <CommandGroup heading={existingHeading}>
                {filtered.map((opt) => (
                  <CommandItem
                    key={opt}
                    value={opt}
                    onSelect={() => handleSelect(opt)}
                    data-testid={`${testIdPrefix}-option-${opt}`}
                    className="text-white aria-selected:bg-slate-800 aria-selected:text-amber-400"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === opt ? "opacity-100 text-amber-500" : "opacity-0"
                      )}
                    />
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showCreateRow && (
              <>
                <CommandSeparator className="bg-slate-800" />
                <CommandGroup>
                  <CommandItem
                    value={`__create__${trimmedQuery}`}
                    onSelect={handleCreate}
                    disabled={creating}
                    data-testid={`${testIdPrefix}-create-option`}
                    className="text-amber-400 aria-selected:bg-amber-500/10"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {creating
                      ? `${creatingLabel} “${trimmedQuery}”…`
                      : `${createLabel} “${trimmedQuery}”`}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CreatableCombobox;
